// packages/api/src/hooks/utils/useSocketEvent.ts
//
// Phase 12 analista-queue-kpis-notif Plan 11 (REQ-10).
// Hook utilitário: subscribe a um evento Socket.IO; cleanup ao unmount.
//
// Contract: docs/contracts/ESCR-analista-queue-kpis-notif-05.md.
//
// **Uso típico Plan 12 (war-room realtime):**
// ```tsx
// const queryClient = useQueryClient();
// useSocketEvent<{ proposalId: string }>('proposal.assigned', (payload) => {
//   queryClient.invalidateQueries({ queryKey: queryKeys.analystQueue.all });
//   queryClient.invalidateQueries({
//     queryKey: queryKeys.proposalAnalysis.detail(payload.proposalId),
//   });
// });
// ```
//
// Handler é mantido em ref para evitar re-subscribe a cada render (caller passa
// arrow function inline sem useCallback — pattern comum em React).

import { useEffect, useRef } from 'react';

import { acquireSharedSocket, releaseSharedSocket } from './socket-shared';

/**
 * Subscribe a um evento Socket.IO; cleanup automático ao unmount.
 *
 * No-op quando:
 *   - `eventName` é empty
 *   - usuário não está autenticado (sem access token)
 *
 * Re-subscribe automático quando `eventName` muda. Handler atualizado via ref
 * (callers não precisam memoizar).
 */
export function useSocketEvent<TPayload = unknown>(
  eventName: string,
  handler: (payload: TPayload) => void,
): void {
  const handlerRef = useRef(handler);

  // Atualiza ref sem trigger re-subscribe (caller passa closure inline).
  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(() => {
    if (!eventName) return;

    const socket = acquireSharedSocket();
    if (!socket) return;

    const wrapped = (payload: TPayload) => {
      handlerRef.current(payload);
    };

    socket.on(eventName, wrapped);

    return () => {
      try {
        socket.off(eventName, wrapped);
      } catch {
        // ignore
      }
      releaseSharedSocket();
    };
  }, [eventName]);
}
